import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const Footer: React.FC = () => {
    return (
        <Box
            component="footer"
            sx={{
                py: 1,
                px: 3,
                backgroundColor: '#2c3e50',
                color: 'white',
                borderTop: '3px solid #DAA520',
                maxHeight: '300px',
                height: 'fit-content',
                overflow: 'hidden'
            }}
        >
            <Box sx={{ maxWidth: '1536px', mx: 'auto', px: 0 }}>
                <Box
                    display="flex"
                    flexDirection={{ xs: 'column', lg: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
                    gap={2}
                >
                    {/* Left Section - Just the text */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'white',
                            fontWeight: 'medium',
                            fontSize: '1rem'
                        }}
                    >
                        Feedback & Inquiries
                    </Typography>

                    {/* Right Section - Legal & Copyright */}
                    <Box
                        sx={{
                            textAlign: { xs: 'left', lg: 'right' },
                            width: { xs: '100%', lg: 'auto' }
                        }}
                    >
                        {/* Copyright */}
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            &copy; 2025 Nepa Consultancy, LLC. All rights reserved.
                        </Typography>

                        {/* Trademark */}
                        <Typography
                            variant="caption"
                            sx={{
                                mb: 1,
                                color: '#B0BEC5',
                                display: 'block'
                            }}
                        >
                            MEMER™, MEMER 么么儿™ and associated logos are trademarks of Nepa Consultancy, LLC.
                        </Typography>

                        {/* Legal Links */}
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                component="a"
                                href="https://app.memer.world/docs/legal-disclaimer"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#DAA520',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    mr: 1,
                                    '&:hover': {
                                        color: '#C19B2C',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Legal Disclaimer
                            </Typography>
                            <Typography component="span" sx={{ color: '#B0BEC5', mx: 0.5 }}>|</Typography>
                            <Typography
                                component="a"
                                href="https://app.memer.world/docs/intellectual-property"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#DAA520',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    mx: 1,
                                    '&:hover': {
                                        color: '#C19B2C',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Intellectual Property
                            </Typography>
                            <Typography component="span" sx={{ color: '#B0BEC5', mx: 0.5 }}>|</Typography>
                            <Typography
                                component="a"
                                href="https://app.memer.world/docs/tax-implications"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#DAA520',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    mx: 1,
                                    '&:hover': {
                                        color: '#C19B2C',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Tax Implications
                            </Typography>
                            <Typography component="span" sx={{ color: '#B0BEC5', mx: 0.5 }}>|</Typography>
                            <Typography
                                component="a"
                                href="https://app.memer.world/docs/terms-of-service"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#DAA520',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    mx: 1,
                                    '&:hover': {
                                        color: '#C19B2C',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Terms of Service
                            </Typography>
                            <Typography component="span" sx={{ color: '#B0BEC5', mx: 0.5 }}>|</Typography>
                            <Typography
                                component="a"
                                href="https://app.memer.world/docs/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#DAA520',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                    ml: 1,
                                    '&:hover': {
                                        color: '#C19B2C',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Privacy Policy
                            </Typography>
                        </Box>

                        {/* Legal Disclaimers */}
                        <Box
                            sx={{
                                fontSize: '0.75rem',
                                color: '#B0BEC5',
                                borderTop: '1px solid #455A64',
                                pt: 1,
                                maxWidth: { xs: '100%', lg: '600px' },
                                ml: { xs: 0, lg: 'auto' }
                            }}
                        >
                            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                <strong style={{ color: 'white' }}>Tax Information:</strong> MEMER DAO charitable activities are not tax-deductible. This dashboard provides operational tools only and is not tax advice. We are not financial advisors, tax accountants, or attorneys. Please consult qualified professionals for tax guidance.
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                                <strong style={{ color: 'white' }}>Legal Status:</strong> $MEMER is a utility token for community governance, not a security or investment. DAO participation is civic engagement, not charitable giving for tax purposes.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* MEMER Branding Section */}
                <Box
                    sx={{
                        borderTop: '1px solid #455A64',
                        mt: 1.5,
                        pt: 1
                    }}
                >
                    <Box
                        display="flex"
                        flexDirection={{ xs: 'column', md: 'row' }}
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <img
                                src="/img/MemerLogo.png"
                                alt="MEMER Logo"
                                style={{ width: 24, height: 24, borderRadius: '50%' }}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 'bold',
                                    color: '#DAA520',
                                    fontSize: '1.125rem',
                                    fontFamily: '"Noto Serif SC", serif'
                                }}
                            >
                                MEMER 么么儿
                            </Typography>
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#B0BEC5',
                                display: { xs: 'none', md: 'inline' }
                            }}
                        >
                            ·
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: '#B0BEC5' }}
                        >
                            A panda with purpose - Building global mutual aid networks
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Footer;
